import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import CalendarUtils from 'services/CalendarUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ProgressMessage from 'components/Utils/ProgressMessage';
import './style.scss';
import Icon from 'components/Utils/NewIcon/index';
import PerformanceUtils from 'services/PerformanceUtils';
import _ from 'lodash';
import SelectClinic from 'scenes/HomePatient/AppointmentBooking/SelectClinic';
import AppointmentUtils from 'services/AppointmentUtils';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import DefaultPatientModal from 'components/Utils/DefaultPatientModal';
import { HorizontalCalendar } from './Components/HorizontalCalendar';
import noSlotsImg from 'assets/img/empty-appointments.webp';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import PhysicianUtils from 'services/PhysicianUtils';
import { PhysicianImage } from 'components/Common/UserImage';
import MiscUtils from 'services/MiscUtils';
import { PractitionerPopupContent } from 'scenes/ChoosePrimary';
import DefaultPopup from 'components/Utils/DefaultPopup';
import { PhysicianPhysicianTypeEnum } from 'generated/api';
import { CancellationListForm } from 'components/Schedule/CancellationListForm';
import { CustomLoadingDataSpinner } from 'components/Common/LoadingDataSpinner';

const FORMAT = 'h:mm A on MMMM Do';

/**
 * @param {any} props
 */
const SlotSchedule = ({ patient, appointment, goBack, ...props }) => {
  const dispatch = useAppDispatch();

  const physicians = useAppSelector((state) => state.physicians.byId);
  const clinics = useAppSelector((state) => state.resource.clinics);
  const options = useAppSelector((state) => state.appointments.options);

  const patientId = patient.id;

  useEffect(() => {
    dispatch(api.Appointments.getBookingFlowOptions({ patientId }));
  }, [dispatch, patientId]);

  const loading = useIsLoading(api.Appointments.getAppointmentSlots);

  const [calendarStartingDate, setCalendarStartingDate] = useState(
    moment().tz(CalendarUtils.getTargetTimezone()),
  );

  function onSelect(slot) {
    let physician_id = slot.physicians.includes(patient.primary_physician)
      ? patient.primary_physician
      : _.sample(slot.physicians);

    props.onSelect(slot, physician_id);
  }

  const clinic = clinics.find((clinic) => clinic.id === appointment.clinic_id);

  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(undefined);
  const [date, setDate] = useState(appointment.date || moment().unix());
  const [clinicModal, setClinicModal] = useState(false);
  const [practitionerPopup, setPractitionerPopup] = useState();
  const isLookingForNextPrimarySlot = useIsLoading(api.Appointments.getNextAvailableSlot);
  const [hasLookedForNextPrimarySlot, setHasLookedForNextPrimarySlot] = useState(false);
  const [nextPrimarySlot, setNextPrimarySlot] = useState();
  const [hasLoadedSlots, setHasLoadedSlots] = useState(false);

  const isSameClinicAsPrimary =
    physicians[patient?.primary_physician]?.clinic_id === appointment.clinic_id;

  async function getNextPrimarySlot(appointment) {
    if (nextPrimarySlot && nextPrimarySlot.start <= date) {
      return;
    }
    const slot = await dispatch(
      api.Appointments.getNextAvailableSlot({
        appointmentSlotsRequest: {
          ...appointment,
          physician_id: patient.primary_physician,
        },
      }),
    );

    setNextPrimarySlot(slot);
  }

  const concern = useMemo(() => {
    return options
      .map(({ concerns = [] }) => concerns.find(({ id }) => id === appointment.concern_id))
      .find((concern) => !!concern);
  }, [appointment, options]);

  const showPrimary =
    (concern?.physician_types?.length === 0 ||
      concern?.physician_types?.includes(PhysicianPhysicianTypeEnum.NursePractitioner)) &&
    isSameClinicAsPrimary &&
    !concern?.no_primary_physician;

  useEffect(() => {
    if (showPrimary && !hasLookedForNextPrimarySlot) {
      const primarySlot = slots?.find((it) => it.physicians.includes(patient.primary_physician));
      if (!primarySlot) {
        setHasLookedForNextPrimarySlot(true);
        getNextPrimarySlot(appointment);
      }
    }
  }, [slots, patient.primary_physician, showPrimary, appointment, hasLookedForNextPrimarySlot]);

  function onSelectedClinic() {
    setClinicModal(false);
  }

  const onlyPrimary = !!concern?.only_primary_physician;

  const showClinic = useMemo(() => {
    // Don't show clinic selector if concern restricts clinics
    if (concern?.clinics?.length > 0) {
      return concern.clinics.length > 1; // Only show if multiple clinics allowed
    }

    // Original logic for non-restricted appointments
    return (
      !onlyPrimary &&
      clinics.length > 1 &&
      !!clinic &&
      ((!AppointmentUtils.isHomeAppointment(appointment) &&
        !AppointmentUtils.isTelemedicineAppointment(appointment)) ||
        !patient.clinic_id)
    );
  }, [concern, onlyPrimary, clinics, clinic, appointment, patient]);

  useEffect(() => {
    let slotAppointment = {
      ...appointment,
      physician_id: undefined,
      date,
    };
    setHasLoadedSlots(false);
    dispatch(
      api.Appointments.getAppointmentSlots({
        appointmentSlotsRequest: slotAppointment,
      }),
      { errorTitle: 'Available Slots' },
    ).then((slots) => {
      if (slots) {
        setSlots(slots);
        setHasLoadedSlots(true);
      }
    });
  }, [appointment, dispatch, date, showClinic]);

  const onDateSelected = useCallback(
    (date) => {
      setDate(date);
      setSlot(undefined);
    },
    [setDate, setSlot],
  );

  function onSelectSlot() {
    onSelect({ ...slot, physicians: [slot.physicianId] });
  }

  const slotsByPhysician = useMemo(() => {
    let sortedSlots = PerformanceUtils.sortBy([...slots], ['start'], false);
    const grouped = {};

    // First add primary physician slots if they exist
    if (patient?.primary_physician && isSameClinicAsPrimary) {
      grouped[patient.primary_physician] = [];
    }

    // Then add all other slots
    sortedSlots.forEach((it) => {
      it.physicians.forEach((physicianId) => {
        if (!grouped[physicianId]) {
          grouped[physicianId] = [];
        }
        grouped[physicianId].push({
          ...it,
          isSelected: slot?.start === it.start && slot.physicianId === physicianId,
        });
      });
    });

    // Create new ordered object with primary physician first
    const orderedGrouped = {};

    // First add primary physician if exists
    if (showPrimary && patient?.primary_physician) {
      orderedGrouped[patient.primary_physician] = cleanUpSlots(
        grouped[patient.primary_physician],
        appointment,
      );
    }

    // Then add all other physicians, sorted by number of slots in descending order
    Object.entries(grouped)
      .filter(([physicianId]) => physicianId !== patient?.primary_physician)
      .sort(([, slotsA], [, slotsB]) => slotsB.length - slotsA.length)
      .forEach(([physicianId, slots]) => {
        orderedGrouped[physicianId] = cleanUpSlots(slots, appointment);
      });

    return orderedGrouped;
  }, [slots, slot, patient?.primary_physician, isSameClinicAsPrimary, appointment, showPrimary]);

  function cleanUpSlots(slots = [], appointment) {
    if (slots.length === 0) return slots;
    if (appointment.type !== AppointmentUtils.getAppointmentTypes().lab) {
      return slots;
    }

    const cleanedSlots = [];
    let i = 0;

    while (i < slots.length) {
      const currentSlot = slots[i];
      const nextSlot = slots[i + 1];
      const nextNextSlot = slots[i + 2];

      // Check if we have 3 consecutive slots (15 minutes)
      if (areConsecutiveSlots(currentSlot, nextSlot, nextNextSlot)) {
        cleanedSlots.push(currentSlot);
        i += 3; // Skip next 2 slots
      }
      // Check if we have 2 consecutive slots (10 minutes)
      else if (areConsecutiveSlots(currentSlot, nextSlot)) {
        cleanedSlots.push(currentSlot);
        i += 2; // Skip next slot
      }
      // Single slot (5 minutes)
      else {
        cleanedSlots.push(currentSlot);
        i += 1;
      }
    }

    return cleanedSlots;
  }

  function areConsecutiveSlots(...slots) {
    // Filter out undefined slots
    const validSlots = slots.filter(Boolean);

    if (validSlots.length <= 1) return false;

    // Check if all slots are consecutive (5 minutes apart)
    for (let i = 0; i < validSlots.length - 1; i++) {
      const currentStart = moment.unix(validSlots[i].start);
      const nextStart = moment.unix(validSlots[i + 1].start);

      // Check if slots are 5 minutes apart
      if (nextStart.diff(currentStart, 'minutes') !== 5) {
        return false;
      }
    }

    return true;
  }

  const buttonName = slot ? CalendarUtils.format(slot.start, FORMAT) : undefined;

  const popupHandler = useMemo(() => {
    return [!!practitionerPopup, setPractitionerPopup];
  }, [practitionerPopup]);

  const noSlotsAtAll = useMemo(() => {
    return hasLoadedSlots && Object.values(slotsByPhysician).every((slots) => slots.length === 0);
  }, [slotsByPhysician, hasLoadedSlots]);

  const hasSlots = useMemo(() => {
    return slots.length > 0;
  }, [slots]);

  const anyAvailable = !!slots.find((slot) => slot.physicians.length > 0);
  const showingOnlyPrimaryFutureSlot = !onlyPrimary && noSlotsAtAll && nextPrimarySlot;
  const chosenDeliveryOption =
    appointment.delivery_option_index != null
      ? concern?.delivery_options?.[appointment.delivery_option_index]
      : undefined;
  const skipFollowUp = !!chosenDeliveryOption?.skip_follow_up;
  const followUpConcern = skipFollowUp ? undefined : concern?.follow_up_concern;
  const hasPrimary = !!patient.primary_physician;

  return (
    <>
      <div className="slot-schedule">
        <DefaultPopup
          title="Select Primary Practitioner"
          className="select-primary-popup"
          openHandler={popupHandler}
        >
          <PractitionerPopupContent
            suggestion={{ physician: practitionerPopup }}
            handler={popupHandler}
          />
        </DefaultPopup>
        <div className="top-info">
          {onlyPrimary && hasPrimary && (
            <div className="only-primary-message">
              <Icon src="circle-info" width="20px" />
              <span>
                Only your primary practitioner is available for "{concern.text}" appointments.
              </span>
            </div>
          )}
          {showClinic && (
            <div className="clinic-selector">
              <div className="header">
                <span className="label">Selected Location</span>
                <button className="location-button" onClick={() => setClinicModal(true)}>
                  <Icon src="location-crosshairs" className="icon" width="15px" />
                  See Other Locations
                </button>
              </div>
              <div className="clinic-info">
                <Icon src="location-dot" className="location-icon" />
                <div className="details">
                  <div className="name">{clinic.name}</div>
                  <div className="address">
                    {MiscUtils.getFullAddress(clinic.address, false, ['state', 'country'])}
                  </div>
                </div>
              </div>
            </div>
          )}
          <HorizontalCalendar
            key={calendarStartingDate?.unix()}
            startingDate={calendarStartingDate}
            selectedDate={moment.unix(date).tz(CalendarUtils.getTargetTimezone())}
            onDateSelected={onDateSelected}
            disabledPastDays={true}
            disabledWeekends={true}
          />
        </div>
        <div className="content">
          <ProgressMessage size={35} show={loading} className="my-5" />
          {!loading && (
            <div className="practitioners-slots">
              {Object.entries(slotsByPhysician).map(([physicianId, physicianSlots]) => {
                const isPrimary = physicianId === patient.primary_physician;
                const hasSlots = physicianSlots?.length > 0;
                const hasFollowUpSlots = !!physicianSlots?.find((it) => it.has_follow_up);

                if (!hasSlots && !isPrimary) {
                  return null;
                }

                return (
                  <div key={physicianId} className="practitioner-section">
                    <div className="practitioner-header">
                      <PhysicianImage user={physicians[physicianId]} size={45} />
                      <div className="practitioner-info">
                        <span className="practitioner-name">
                          {PhysicianUtils.getName(physicians[physicianId])}
                        </span>
                        {physicianId === patient.primary_physician && (
                          <span className="primary-badge">
                            <Icon src="star" width="10px" />
                            Your Primary NP
                          </span>
                        )}
                      </div>
                      <div
                        className="info-icon"
                        onClick={() => setPractitionerPopup(physicians[physicianId])}
                      >
                        <Icon src="circle-info" width="16px" />
                      </div>
                    </div>
                    <div className="slots-wrapper">
                      {!hasSlots && (
                        <span className="no-slots-wrapper">
                          <div className="no-slots is-info">
                            <Icon src="circle-info" width="16px" />
                            &nbsp; No slots available
                          </div>
                          {isPrimary && isLookingForNextPrimarySlot && (
                            <div className="no-slots">
                              <CustomLoadingDataSpinner
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  color: '#2693d1',
                                  marginRight: '1ch',
                                }}
                                loading
                              />
                              Looking for the next available slot...
                            </div>
                          )}
                          {isPrimary && !isLookingForNextPrimarySlot && nextPrimarySlot && (
                            <>
                              <div className="text-center mb-2">
                                {' '}
                                Your practitioners' next available slot is on{' '}
                              </div>
                              <Slot
                                key={`${nextPrimarySlot.start}-${physicianId}`}
                                slot={{
                                  ...nextPrimarySlot,
                                  isSelected:
                                    slot?.start === nextPrimarySlot.start &&
                                    slot.physicianId === patient.primary_physician,
                                }}
                                onClick={() => {
                                  setDate(nextPrimarySlot.start);
                                  setCalendarStartingDate(
                                    moment
                                      .unix(nextPrimarySlot.start)
                                      .tz(CalendarUtils.getTargetTimezone()),
                                  );
                                }}
                                isSelected={nextPrimarySlot.isSelected}
                                showDate
                              />
                            </>
                          )}
                        </span>
                      )}
                      {hasSlots &&
                        physicianSlots.map((slot) => (
                          <Slot
                            key={`${slot.start}-${physicianId}`}
                            followUpIcon={followUpConcern?.icon}
                            slot={slot}
                            onClick={() => setSlot({ ...slot, physicianId })}
                            isSelected={slot.isSelected}
                          />
                        ))}
                    </div>
                    {followUpConcern &&
                      followUpConcern.options?.is_optional &&
                      followUpConcern.icon &&
                      followUpConcern.explanation &&
                      hasFollowUpSlots && (
                        <div className="follow-up-explanation">
                          <Icon src={followUpConcern.icon} /> {followUpConcern.explanation}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          )}
          {!loading && !anyAvailable && Object.keys(slotsByPhysician).length === 0 && (
            <div className="no-dates">
              <img src={noSlotsImg} alt="No availability" width="200px" />
              <div className="item-not-available">
                <div className="content">
                  <div className="row">
                    <Icon src="circle-info" />
                    <span>
                      No availability found on <b>{CalendarUtils.format(date, 'MMMM Do')}</b>
                    </span>
                  </div>
                  <div className="column">
                    <span className="mt-1">Please select another date above</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {showingOnlyPrimaryFutureSlot && (
            <div className="other-nps-availability-notice">
              <Icon src="lightbulb" className="tip-icon" />
              <div className="notice-content">
                <h4>Want to see other practitioners?</h4>
                <p>
                  There are no available appointments for any practitioner on{' '}
                  {CalendarUtils.format(date, 'MMMM Do')}. Try selecting different dates to see
                  availability with other practitioners who may have slots sooner than your primary.
                </p>
              </div>
            </div>
          )}
          <JoinCancellationList
            patient={patient}
            hasSlots={hasSlots}
            loading={loading}
            appointment={appointment}
          />
        </div>
        <DefaultPatientModal
          className="select-clinic-modal"
          title="Select Clinic"
          wrap={true}
          padding={false}
          content={
            <SelectClinic
              patient={patient}
              onSuccess={onSelectedClinic}
              buttonText="Select Clinic"
              allowedClinics={concern?.clinics}
            />
          }
          isOpen={!!clinicModal}
          onRequestClose={() => setClinicModal(false)}
        />
      </div>
      {!loading && (
        <div className="confirm-button-container">
          <DefaultButton
            className="blue"
            id="slot-confirm-button"
            style={{ width: '100%' }}
            name={slot ? `Select ${buttonName}` : 'Select a time slot'}
            onClick={onSelectSlot}
            disabled={!slot}
          />
        </div>
      )}
    </>
  );
};

// Define questions structure - exported for reuse in staff CancellationList
// Cancellation-list questions/labels now live with the shared form component;
// re-exported here so the existing staff-side import keeps resolving.
export {
  CANCELLATION_LIST_QUESTIONS,
  FIELD_LABELS,
} from 'components/Schedule/CancellationListForm';

const JoinCancellationList = ({ patient, hasSlots, loading, appointment }) => {
  const [cancellationListModal, setCancellationListModal] = useState(false);

  if (loading || hasSlots) {
    return null;
  }

  return (
    <>
      <div className="other-nps-availability-notice">
        <Icon src="bell" className="tip-icon" />
        <div className="notice-content">
          <h4>Can't find a suitable time?</h4>
          <p>Join our cancellation list to be notified when appointments become available.</p>
          <DefaultButton
            className="blue mt-2"
            name="Join Cancellation List"
            onClick={() => setCancellationListModal(true)}
            style={{ width: '100%', maxWidth: '300px' }}
          />
        </div>
      </div>
      <DefaultPatientModal
        className="cancellation-list-modal"
        title="Join Cancellation List"
        wrap={true}
        padding={true}
        content={
          <CancellationListForm
            patient={patient}
            appointment={appointment}
            onCancel={() => setCancellationListModal(false)}
            onSubmitted={() => setCancellationListModal(false)}
          />
        }
        isOpen={!!cancellationListModal}
        onRequestClose={() => setCancellationListModal(false)}
      />
    </>
  );
};

const Slot = ({ slot, onClick, showDate = false, followUpIcon }) => {
  let className = 'slot';

  let active = slot.physicians.length > 0;

  if (active) className += ' active';
  if (slot.isSelected) className += ' selected';

  function onClickedSlot() {
    if (active) onClick(slot);
  }

  return (
    <div className={className} onClick={onClickedSlot}>
      {CalendarUtils.format(slot.start, showDate ? 'dddd, MMMM Do h:mm A' : 'h:mm A')}
      {slot.has_follow_up && followUpIcon && (
        <Icon className="follow-up-indicator" src="syringe" width="15px" height="15px" />
      )}
    </div>
  );
};

SlotSchedule.propTypes = {
  physician: PropTypes.object,
  appointment: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default SlotSchedule;
